import type { PrintedColor, PrintColorRecord } from "../shared/records";
import { PRINTED_COLORS } from "../shared/records";
import { MEASURE_COLUMN_IDS, type MeasureColumnId } from "../shared/measure-columns";
import type { PaperUsageRow } from "./types";

/**
 * Pure transformations (convention section 24): input → function → output.
 * No Redux, no API calls, no ECharts instance access — the component feeds
 * selection state in and applies the results.
 */

/** Visible categories in the chart's dataZoom scroll window. */
export const PAPER_USAGE_WINDOW = 12;

const FLAT_LENGTH_FIELD = {
  Monochrome: "monoLength",
  Color: "colorLength",
  "Front-Monochrome": "frontMonoLength",
  "Back-Monochrome": "backMonoLength",
} as const;

/**
 * Aggregates the shared (width × color) records per paper width. The
 * per-color breakdowns are carried along so the chart can split each bar
 * blue/grey and the table can show a color's values when it is selected.
 */
export function aggregateRecordsToWidthRows(records: PrintColorRecord[]): PaperUsageRow[] {
  const byWidth = new Map<number, PaperUsageRow>();
  for (const record of records) {
    let row = byWidth.get(record.width);
    if (!row) {
      row = {
        width: record.width,
        printedLength: 0,
        jobCount: 0,
        cLength: 0,
        mLength: 0,
        yLength: 0,
        kLength: 0,
        micrLength: 0,
        monoLength: 0,
        colorLength: 0,
        frontMonoLength: 0,
        backMonoLength: 0,
        byColor: Object.fromEntries(
          PRINTED_COLORS.map((color) => [
            color,
            {
              printedLength: 0,
              jobCount: 0,
              cLength: 0,
              mLength: 0,
              yLength: 0,
              kLength: 0,
              micrLength: 0,
            },
          ]),
        ) as PaperUsageRow["byColor"],
      };
      byWidth.set(record.width, row);
    }
    row.printedLength += record.printedLength;
    row.jobCount += record.jobCount;
    row.cLength += record.cLength;
    row.mLength += record.mLength;
    row.yLength += record.yLength;
    row.kLength += record.kLength;
    row.micrLength += record.micrLength;
    row[FLAT_LENGTH_FIELD[record.color]] += record.printedLength;
    const breakdown = row.byColor[record.color];
    breakdown.printedLength += record.printedLength;
    breakdown.jobCount += record.jobCount;
    breakdown.cLength += record.cLength;
    breakdown.mLength += record.mLength;
    breakdown.yLength += record.yLength;
    breakdown.kLength += record.kLength;
    breakdown.micrLength += record.micrLength;
  }
  return [...byWidth.values()].sort((a, b) => a.width - b.width);
}

/**
 * ORIGINAL (unfiltered) max of each measure column — the progress scale.
 * The table's fills use these even when a color is selected (requirement:
 * progress vẫn lấy max là giá trị ban đầu của mỗi cột), so bars never
 * rescale when the displayed values change.
 */
export function columnMaxima(rows: PaperUsageRow[]): Record<MeasureColumnId, number> {
  const maxima = {} as Record<MeasureColumnId, number>;
  for (const columnId of MEASURE_COLUMN_IDS) {
    maxima[columnId] = Math.max(...rows.map((row) => row[columnId]), 1);
  }
  return maxima;
}

/**
 * Table rows carrying the DISPLAYED measure values: the per-width totals —
 * or, when a printed color is selected, THAT COLOR's value per column (ink
 * columns show the color's INK usage, Job Count its jobs). Cell renderers,
 * header sorting and the footer's total row all read these fields, so they
 * stay consistent with what the cells show; progress still scales by the
 * ORIGINAL column maxima from `columnMaxima`.
 */
export function toDisplayRows(
  rows: PaperUsageRow[],
  selectedColor: PrintedColor | null,
): PaperUsageRow[] {
  if (selectedColor === null) return rows;
  return rows.map((row) => ({
    ...row,
    printedLength: row.byColor[selectedColor].printedLength,
    jobCount: row.byColor[selectedColor].jobCount,
    cLength: row.byColor[selectedColor].cLength,
    mLength: row.byColor[selectedColor].mLength,
    yLength: row.byColor[selectedColor].yLength,
    kLength: row.byColor[selectedColor].kLength,
    micrLength: row.byColor[selectedColor].micrLength,
  }));
}

/**
 * Chart dataset for the EChart wrapper (category = width). When a printed
 * color is selected, the bar splits into a stacked pair — `selectedColorLength`
 * (blue bottom) + `otherLength` (grey top) — whose sum is always
 * `printedLength`, so the column height never changes.
 */
export function buildPaperUsageDataset(
  points: PaperUsageRow[],
  selectedColor: PrintedColor | null = null,
): {
  dimensions: string[];
  source: Array<PaperUsageRow & { selectedColorLength: number; otherLength: number }>;
} {
  return {
    dimensions: [
      "width",
      "printedLength",
      "jobCount",
      "cLength",
      "mLength",
      "yLength",
      "monoLength",
      "colorLength",
      "frontMonoLength",
      "backMonoLength",
      "selectedColorLength",
      "otherLength",
    ],
    source: points.map((point) => {
      const selectedColorLength = selectedColor
        ? point.byColor[selectedColor].printedLength
        : 0;
      return {
        ...point,
        selectedColorLength,
        otherLength: point.printedLength - selectedColorLength,
      };
    }),
  };
}

/**
 * DataZoom percentages centering the given category index in a
 * `window`-category viewport (table → chart auto-scroll, requirement 3)
 * now live in the library: `zoomCenterPct` from `echart-table-library`.
 */

/** Dataset index of a selected paper width (selection is stored as width — the stable business id). */
export function findIndexByWidth(points: PaperUsageRow[], width: number | null): number | null {
  if (width === null) return null;
  const index = points.findIndex((point) => point.width === width);
  return index === -1 ? null : index;
}

// Formatters live in shared/utils/format (also used by the app callbacks);
// column headers/labels for the measure columns live in
// shared/measure-columns.
